import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { Avatar } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { navPara, routeTitle } from "./navigation";
import { AMBITO_NEGOCIO } from "../lib/constants";
import SelectorDeNegocio from "./SelectorDeNegocio";
import CampanaDeAvisos from "../components/CampanaDeAvisos";

/* =========================================================================
   ARMAZÓN DEL PANEL
   Rail fijo + topbar pegajoso + área de contenido. En pantallas angostas el
   rail pasa a ser un cajón con velo, porque un panel de administración se
   consulta desde el celular más de lo que se admite.
   ======================================================================== */

const COLLAPSE_KEY = "vpy.rail.collapsed";

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { user, ambito, area, nivel, puedeVer, puedeGestionar, logout } =
    useAuth();
  const esNegocio = ambito === AMBITO_NEGOCIO;

  // El menú se recorta al área de quien mira. Ocultar no es proteger: el
  // servidor rechaza igual la sección si alguien escribe la URL a mano.
  const secciones = navPara(ambito, puedeVer, puedeGestionar);
  const { resolved, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* almacenamiento bloqueado */
    }
  }, [collapsed]);

  /*
   * Al navegar se cierra el cajón móvil; si no, queda tapando la pantalla
   * recién abierta.
   *
   * Comparando la ruta anterior en el render en vez de con un efecto: así el
   * cajón ya sale cerrado en el primer pintado de la pantalla nueva, en lugar
   * de aparecer abierto un fotograma y cerrarse después.
   */
  const [rutaPrevia, setRutaPrevia] = useState(location.pathname);

  if (rutaPrevia !== location.pathname) {
    setRutaPrevia(location.pathname);
    setMobileOpen(false);
    setMenuOpen(false);
  }

  // Un clic fuera cierra el menú de usuario.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const salir = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="shell"
      data-collapsed={collapsed}
      data-mobile-open={mobileOpen}
    >
      {/*
        SALTAR AL CONTENIDO. Es el primer elemento que recibe foco y solo se ve
        al recibirlo. Sin él, quien navega con teclado atraviesa las veintinueve
        entradas del menú antes de llegar a la tabla — en cada carga de página.
      */}
      <a href="#contenido" className="saltar-al-contenido">
        Saltar al contenido
      </a>

      <div className="rail-scrim" onClick={() => setMobileOpen(false)} />

      <aside className="rail">
        <div className="rail-brand">
          {/* Colapsada solo queda el isotipo: el logotipo con la palabra no
              se lee a 64 px de ancho. Lo oculta `.rail-name` en el CSS. */}
          <span className="rail-logo" role="img" aria-label="VeciPa'Ya" />
          <div style={{ minWidth: 0 }} className="rail-name">
            <span
              className="marca-logotipo"
              role="img"
              aria-label="VeciPa'Ya"
            />
            {/* El área, no "Administración": ahora hay siete perfiles distintos
                y saber en cuál se está explica por qué el menú es el que es. */}
            {/* Se dice el nivel junto al área: quien solo consulta no ve
                botones de crear, y sin este rótulo parecería que el panel se
                le rompió en vez de que su acceso es de lectura. */}
            {/* Qué se está administrando. Para el tendero es el nombre de su
                local, que cuando tiene dos es el dato más importante de la
                pantalla: sin él, dos paneles idénticos con pedidos distintos. */}
            <div className="rail-role">
              {area?.name ?? (esNegocio ? "Mi negocio" : "Administración")}
              {nivel === "consulta" ? " · solo consulta" : ""}
            </div>
          </div>
        </div>

        <nav className="rail-nav">
          {secciones.map((section, i) => (
            <div key={section.group ?? `s${i}`}>
              {section.group && (
                <div className="nav-group">{section.group}</div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    `nav-item${isActive ? " active" : ""}`
                  }
                >
                  <Icon name={item.icon} size={17} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          {/* El control del menú vive arriba y a la izquierda, fijo: si
              estuviera dentro del rail se movería o desaparecería justo al
              contraerlo, que es cuando más se necesita. En pantalla angosta
              el mismo botón abre el cajón. */}
          <button
            className="btn btn-ghost btn-icon only-desktop"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expandir menú" : "Contraer menú"}
            aria-pressed={collapsed}
            title={collapsed ? "Expandir menú" : "Contraer menú"}
          >
            <Icon name="sidebar" size={18} />
          </button>

          <button
            className="btn btn-ghost btn-icon only-mobile"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            <Icon name="menu" size={19} />
          </button>

          <div className="crumb">
            <span className="crumb-raiz">
              {esNegocio ? "Mi negocio" : "Conjunto"}
            </span>{" "}
            <Icon name="chevronRight" size={12} />{" "}
            <b>{routeTitle(location.pathname, ambito)}</b>
          </div>

          <div
            className="right row gap-2"
            style={{ position: "relative" }}
            ref={menuRef}
          >
            {/* Sólo aparece con más de un local. Con uno solo sería un
                desplegable de una opción: ocupa sitio y no decide nada. */}
            {esNegocio && <SelectorDeNegocio />}

            {/* La campana va en la barra y no en una seccion del menu: un
                aviso sirve mientras es nuevo, y si hay que ir a buscarlo a
                una pantalla, para cuando alguien entra ya era historia. */}
            <CampanaDeAvisos />

            <button
              className="btn btn-ghost btn-icon"
              onClick={toggle}
              aria-label={
                resolved === "dark"
                  ? "Cambiar a tema claro"
                  : "Cambiar a tema oscuro"
              }
              title={resolved === "dark" ? "Tema claro" : "Tema oscuro"}
            >
              <Icon name={resolved === "dark" ? "sun" : "moon"} size={17} />
            </button>

            <span className="topbar-sep" aria-hidden="true" />

            <button
              className="user-chip"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <Avatar name={user?.name} />
              <span className="user-chip-text">
                <span className="user-chip-name">
                  {user?.name ?? "Administrador"}
                </span>
                <span className="user-chip-role">{user?.email}</span>
              </span>
              <Icon name="chevronDown" size={14} className="faint" />
            </button>

            {/* Salir directo, sin abrir el menú: es la acción que más se
                repite y esconderla tras un desplegable la vuelve un trámite. */}
            <button
              className="btn btn-ghost btn-icon btn-salir"
              onClick={salir}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <Icon name="logout" size={17} />
            </button>

            {menuOpen && (
              <div className="menu" role="menu">
                <div className="menu-head">
                  <Avatar name={user?.name} />
                  <span style={{ minWidth: 0 }}>
                    <span className="user-chip-name truncate">
                      {user?.name}
                    </span>
                    <span className="user-chip-role truncate">
                      {user?.email}
                    </span>
                  </span>
                </div>
                {/* Ni "Mi cuenta" ni "Ajustes": las dos venían del panel
                    interno y acá no existen esas rutas. Ofrecerlas mandaba a
                    la pantalla de "esta página no existe" desde el menú del
                    propio panel. Lo que sí tiene el tendero es la ficha de su
                    negocio, que es una sección con su entrada en el menú. */}
                {esNegocio && puedeVer("negocio") && (
                  <button
                    className="menu-item"
                    onClick={() => navigate("/mi-negocio")}
                  >
                    <Icon name="store" size={16} /> Datos de mi negocio
                  </button>
                )}
                <button className="menu-item" onClick={toggle}>
                  <Icon name={resolved === "dark" ? "sun" : "moon"} size={16} />
                  {resolved === "dark" ? "Tema claro" : "Tema oscuro"}
                </button>
                <hr className="menu-sep" />
                <button className="menu-item danger" onClick={salir}>
                  <Icon name="logout" size={16} /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="page" id="contenido" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
